import type { AssistantMessage, ChatMessage, ChatStatus, ToolCall } from './types'

/**
 * State managed by the chat reducer. The `messages` array is the
 * canonical render input; `status` drives the input box state.
 *
 * The reducer is a pure function — the hook owns the actual `useState`
 * and feeds events in. This keeps the event-handling logic unit-testable
 * without needing a live Foundry endpoint.
 */
export interface ChatState {
  messages: ChatMessage[]
  status: ChatStatus
  threadId: string | null
}

export const INITIAL_STATE: ChatState = {
  messages: [],
  status: { kind: 'idle' },
  threadId: null,
}

/**
 * Subset of Foundry SSE events the reducer cares about. Foundry emits more
 * (thread.run.step.created without a tool_call type, thread.run.in_progress,
 * etc.) — we treat anything not listed here as a no-op rather than failing
 * loudly, per Microsoft's "handle unknown events gracefully" guidance.
 */
export type StreamEvent =
  | { kind: 'thread.run.created'; runId: string }
  | { kind: 'thread.message.created'; messageId: string }
  | { kind: 'thread.message.delta'; messageId: string; textDelta: string }
  | { kind: 'thread.message.completed'; messageId: string }
  | { kind: 'thread.run.step.tool_call_started'; stepId: string; toolName: string }
  | { kind: 'thread.run.step.tool_call_args_delta'; stepId: string; argsDelta: string }
  | { kind: 'thread.run.step.tool_call_completed'; stepId: string; resultJson: string }
  | { kind: 'thread.run.completed' }
  | { kind: 'thread.run.failed'; message: string }
  | { kind: 'stream.error'; message: string }
  // Local-only event injected by the hook before the network call so we
  // render the user's message immediately, not after the first SSE frame.
  | { kind: 'user.message.appended'; messageId: string; text: string }
  // Local-only reset for "New chat".
  | { kind: 'thread.reset' }

export function chatReducer(state: ChatState, event: StreamEvent): ChatState {
  switch (event.kind) {
    case 'thread.reset':
      return INITIAL_STATE

    case 'user.message.appended':
      return {
        ...state,
        messages: [...state.messages, { id: event.messageId, role: 'user', text: event.text }],
        status: { kind: 'sending' },
      }

    case 'thread.run.created':
      // The Responses API conflates "this turn's id" and "the value to
      // pass as previous_response_id next turn" into the same response.id.
      // Persist it as threadId so the next send() call chains correctly.
      return {
        ...state,
        threadId: event.runId,
        status: { kind: 'streaming', runId: event.runId },
      }

    case 'thread.message.created':
      return ensureCurrentRunBubble(state, event.messageId)

    case 'thread.message.delta':
      return updateAssistantMessage(state, event.messageId, msg => ({
        ...msg,
        text: msg.text + event.textDelta,
      }))

    case 'thread.message.completed':
      return updateAssistantMessage(state, event.messageId, msg => ({
        ...msg,
        isStreaming: false,
      }))

    case 'thread.run.step.tool_call_started': {
      // Responses-API event ordering: tool_call_started fires BEFORE
      // message.created within the same run. Ensure the current run's
      // assistant bubble exists (creating a placeholder if not yet
      // present) so the tool call attaches to this turn — not the
      // previous turn's bubble.
      const next = ensureCurrentRunBubble(state, null)
      return appendToolCallToCurrentRun(next, {
        id: event.stepId,
        name: event.toolName,
        state: 'in_progress',
        argsJson: '',
        resultJson: null,
      })
    }

    case 'thread.run.step.tool_call_args_delta':
      return mutateToolCall(state, event.stepId, tc => ({
        ...tc,
        argsJson: tc.argsJson + event.argsDelta,
      }))

    case 'thread.run.step.tool_call_completed':
      return mutateToolCall(state, event.stepId, tc => ({
        ...tc,
        state: 'completed',
        resultJson: event.resultJson,
      }))

    case 'thread.run.completed':
      return {
        ...state,
        status: { kind: 'idle' },
        messages: state.messages.map(m =>
          m.role === 'assistant' && m.isStreaming ? { ...m, isStreaming: false } : m,
        ),
      }

    case 'thread.run.failed':
      return { ...state, status: { kind: 'error', message: event.message } }

    case 'stream.error':
      return { ...state, status: { kind: 'error', message: event.message } }

    default: {
      // Exhaustiveness check — TypeScript will error if a new StreamEvent variant
      // is added without a case above.
      assertNever(event)
      return state
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function assertNever(_value: never): void {
  // Compile-time exhaustiveness guard; runtime never receives a value.
}

/**
 * Idempotently ensures the current run has an assistant bubble in the
 * message list. Two entry paths into this function:
 *
 * 1. `thread.message.created` arrives with the real messageId. If a
 *    placeholder was already created by an earlier tool_call_started,
 *    update its id to the real one. Otherwise append a new bubble.
 *
 * 2. `thread.run.step.tool_call_started` arrives before the message
 *    item — pass `null` for messageId. We create a placeholder bubble
 *    using `placeholder-<runId>` as the temporary id, which the
 *    `message.created` path later renames to the real messageId.
 *
 * Either way, the resulting state has exactly one assistant bubble
 * tagged with the current `runId`.
 */
function ensureCurrentRunBubble(state: ChatState, messageId: string | null): ChatState {
  if (state.status.kind !== 'streaming') {
    // No active run — shouldn't happen from these event paths, but be
    // defensive. Fall back to appending a fresh bubble with no runId.
    if (messageId === null) return state
    const next: AssistantMessage = {
      id: messageId,
      role: 'assistant',
      runId: '',
      text: '',
      toolCalls: [],
      isStreaming: true,
    }
    return { ...state, messages: [...state.messages, next] }
  }

  const runId = state.status.runId
  const existingIdx = state.messages.findIndex(m => m.role === 'assistant' && m.runId === runId)

  if (existingIdx === -1) {
    // No bubble for this run yet — append one. Use messageId if we have it,
    // otherwise a placeholder keyed on runId that message.created will rename.
    const next: AssistantMessage = {
      id: messageId ?? `placeholder-${runId}`,
      role: 'assistant',
      runId,
      text: '',
      toolCalls: [],
      isStreaming: true,
    }
    return { ...state, messages: [...state.messages, next] }
  }

  if (messageId === null) {
    // Bubble already exists and we have no new id to apply — no-op.
    return state
  }

  // Bubble exists; rename its id to the real messageId (it may have been a
  // placeholder created by an earlier tool_call_started).
  return {
    ...state,
    messages: state.messages.map((m, i) => (i === existingIdx ? { ...m, id: messageId } : m)),
  }
}

function updateAssistantMessage(
  state: ChatState,
  messageId: string,
  fn: (msg: AssistantMessage) => AssistantMessage,
): ChatState {
  return {
    ...state,
    messages: state.messages.map(m => (m.role === 'assistant' && m.id === messageId ? fn(m) : m)),
  }
}

/**
 * Attaches a tool call to the assistant bubble for the current run.
 * Callers must invoke `ensureCurrentRunBubble` first so a target bubble
 * exists.
 */
function appendToolCallToCurrentRun(state: ChatState, toolCall: ToolCall): ChatState {
  if (state.status.kind !== 'streaming') return state
  const runId = state.status.runId
  return {
    ...state,
    messages: state.messages.map(m =>
      m.role === 'assistant' && m.runId === runId
        ? { ...m, toolCalls: [...m.toolCalls, toolCall] }
        : m,
    ),
  }
}

function mutateToolCall(
  state: ChatState,
  stepId: string,
  fn: (tc: ToolCall) => ToolCall,
): ChatState {
  return {
    ...state,
    messages: state.messages.map(m =>
      m.role === 'assistant'
        ? { ...m, toolCalls: m.toolCalls.map(tc => (tc.id === stepId ? fn(tc) : tc)) }
        : m,
    ),
  }
}
